import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Badge,
  Box,
  Button,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Menu,
  Typography,
} from '@mui/material';
import { Notifications as NotificationsIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useCustomerNotifications } from '../../context/CustomerNotificationsContext';
import { useSupportChat } from '../../context/SupportChatContext';
import { formatUtcDateTime } from '../../utils/dateFormatUtils';
import { buildCustomerRoute } from '../../utils/storeUtils';
import { BRAND, BRAND_RADIUS, brandAlpha } from '../../theme/brandTokens';
import PushNotificationControl from '../notifications/PushNotificationControl';
import { openPushPrompt } from '../../utils/pushPrompt';

const isChatNotification = (notification) => {
  const type = String(notification?.type ?? '');
  return type === 'SupportChat' || type === '0';
};

const CustomerNotificationBell = ({ storeId }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { openChat } = useSupportChat();
  const {
    notifications,
    unreadCount,
    markRead,
    markAllRead,
  } = useCustomerNotifications();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleNotificationClick = async (notification) => {
    setAnchorEl(null);
    if (isChatNotification(notification)) {
      openChat();
    } else {
      const target = buildCustomerRoute(storeId, 'my-appointments');
      if (target) navigate(target);
    }
    if (!notification?.isRead) {
      try {
        await markRead(notification.id);
      } catch (error) {
        console.error('Failed to mark customer notification as read', error);
      }
    }
  };

  return (
    <>
      <IconButton
        onClick={(event) => setAnchorEl(event.currentTarget)}
        aria-label={t('customerNotifications.title')}
        sx={{
          flexShrink: 0,
          color: BRAND.textMuted,
          '&:hover': { bgcolor: brandAlpha('accent', 0.1), color: BRAND.accentText },
        }}
      >
        <Badge badgeContent={unreadCount} color="error" max={99}>
          <NotificationsIcon />
        </Badge>
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        disableScrollLock
        PaperProps={{
          sx: {
            mt: 1,
            width: { xs: 'min(100vw - 24px, 360px)', sm: 380 },
            maxHeight: 480,
            borderRadius: BRAND_RADIUS.button,
            border: `1px solid ${BRAND.line}`,
          },
        }}
      >
        <Box sx={{ px: 2, pt: 1.5, pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            {t('customerNotifications.title')}
          </Typography>
          {unreadCount > 0 ? (
            <Button
              size="small"
              onClick={() => markAllRead().catch(() => undefined)}
              sx={{ color: BRAND.accentText, textTransform: 'none' }}
            >
              {t('customerNotifications.markAllRead')}
            </Button>
          ) : null}
        </Box>
        <Box sx={{ px: 2, pb: 1 }} onMouseDown={(event) => event.stopPropagation()}>
          <PushNotificationControl
            audience="customer"
            compact
            onEnableRequest={() => {
              setAnchorEl(null);
              openPushPrompt('customer');
            }}
          />
        </Box>
        <Divider />
        {notifications.length === 0 ? (
          <Box sx={{ px: 2, py: 3 }}>
            <Typography variant="body2" color="text.secondary" align="center">
              {t('customerNotifications.empty')}
            </Typography>
          </Box>
        ) : (
          <List dense disablePadding sx={{ overflowY: 'auto', maxHeight: 320 }}>
            {notifications.map((notification) => (
              <ListItemButton
                key={notification.id}
                onClick={() => handleNotificationClick(notification)}
                sx={{
                  alignItems: 'flex-start',
                  bgcolor: notification.isRead ? 'transparent' : brandAlpha('accent', 0.08),
                }}
              >
                <ListItemText
                  primary={notification.title}
                  secondary={(
                    <>
                      <Typography component="span" variant="body2" color="text.secondary" sx={{ display: 'block' }}>
                        {notification.body}
                      </Typography>
                      <Typography component="span" variant="caption" color="text.disabled">
                        {formatUtcDateTime(notification.createdAtUtc)}
                      </Typography>
                    </>
                  )}
                  primaryTypographyProps={{
                    variant: 'body2',
                    fontWeight: notification.isRead ? 500 : 700,
                  }}
                />
              </ListItemButton>
            ))}
          </List>
        )}
        <Divider />
        <Box sx={{ px: 1, py: 0.5 }}>
          <Button
            fullWidth
            onClick={() => {
              setAnchorEl(null);
              const target = buildCustomerRoute(storeId, 'notifications');
              if (target) navigate(target);
            }}
            sx={{ color: BRAND.accentText, textTransform: 'none', fontWeight: 700 }}
          >
            {t('customerNotifications.seeAll')}
          </Button>
        </Box>
      </Menu>
    </>
  );
};

export default CustomerNotificationBell;
